import { ReactNode } from 'react';
import { Skeleton } from '@mui/material';
import clsx from 'clsx';

import { leadSourceOptions } from '@features/lead-management/constants';

import SourceAvatar from './SourceAvatar';

import { SOURCE_INSIGHT_CARD_COPY } from '../../constants/leadSources';

export interface SourceInsightCardProps {
  title: string;
  sourceName?: string;
  metric?: ReactNode;
  metricAsPill?: boolean;
  isEmpty?: boolean;
  isLoading?: boolean;
  hideSourceLogo?: boolean;
}

const InsightCardSkeleton = () => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center gap-2">
      <Skeleton animation="wave" variant="circular" width={20} height={20} />
      <Skeleton animation="wave" variant="text" width={120} height={20} />
    </div>
    <Skeleton animation="wave" variant="rounded" width={96} height={12} />
  </div>
);

const SourceInsightCard = ({
  title,
  sourceName,
  metric,
  metricAsPill = false,
  isEmpty = false,
  isLoading = false,
  hideSourceLogo = false
}: SourceInsightCardProps) => {
  const renderBody = () => {
    if (isLoading) return <InsightCardSkeleton />;
    if (isEmpty) {
      return (
        <div className="text-lg font-semibold text-cofeeGreyBlue11">
          {SOURCE_INSIGHT_CARD_COPY.emptyMessage}
        </div>
      );
    }

    const sourceNameLabel =
      leadSourceOptions[sourceName as keyof typeof leadSourceOptions];

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          {!hideSourceLogo && <SourceAvatar name={sourceName ?? ''} />}
          <span className="line-clamp-1 break-all text-xl font-semibold text-cofeeGreyBlue11">
            {sourceNameLabel || sourceName}
          </span>
        </div>
        {metricAsPill ? (
          <div
            className={clsx(
              'w-fit rounded-xl bg-cofeeGreen11 px-1 py-px',
              'text-xs font-medium text-cofeeGreen8'
            )}
          >
            {metric}
          </div>
        ) : (
          <div className="text-[14px] text-cofeeNeutral7">{metric}</div>
        )}
      </div>
    );
  };

  return (
    <div
      className={clsx(
        'flex min-h-35 w-full flex-col gap-6 rounded-2xl',
        'bg-white p-6 shadow-boxShadow3'
      )}
    >
      <div className="text-base font-medium text-cofeeNeutral8">{title}</div>
      {renderBody()}
    </div>
  );
};

export default SourceInsightCard;
